import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Divider
} from '@mui/material';
import CalendarIntegration from '../components/CalendarIntegration';

const CalendarTest: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [testData, setTestData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  // Test API connection
  const testApiConnection = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch('https://salon.rockyroadsolutions.com/api/test');
      const data = await response.json();
      setTestData(data);
    } catch (err) {
      console.error('Error testing API connection:', err);
      setError('Failed to connect to API. Make sure the backend server is running.');
    } finally {
      setLoading(false);
    }
  };

  // Test calendar API
  const testCalendarApi = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch('https://salon.rockyroadsolutions.com/api/test/calendar');
      const data = await response.json();
      setTestData(data);
    } catch (err) {
      console.error('Error testing calendar API:', err);
      setError('Failed to connect to calendar API. Make sure the backend server is running.');
    } finally {
      setLoading(false);
    }
  };

  // Mock appointment for testing
  const mockAppointment = {
    id: 'test-123',
    title: 'Test Appointment',
    startTime: new Date(new Date().setHours(new Date().getHours() + 24)),
    endTime: new Date(new Date().setHours(new Date().getHours() + 25)),
    location: 'Bella Vista Salon & Spa',
    description: 'This is a test appointment.'
  };

  // Handle calendar events
  const handleCalendarEvent = (provider: string, action: string, success: boolean) => {
    console.log(`Calendar event: ${provider} ${action} ${success ? 'succeeded' : 'failed'}`);
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', py: 4, px: 2 }}>
      <Typography variant="h4" gutterBottom>
        Calendar Integration Test
      </Typography>
      
      <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          API Connection Test
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <Button 
            variant="contained" 
            onClick={testApiConnection}
            disabled={loading}
          >
            Test API Connection
          </Button>
          
          <Button 
            variant="contained" 
            onClick={testCalendarApi}
            disabled={loading}
          >
            Test Calendar API
          </Button>
        </Box>
        
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress />
          </Box>
        )}
        
        {error && (
          <Box sx={{ bgcolor: '#ffebee', p: 2, borderRadius: 1, mb: 2 }}>
            <Typography color="error">{error}</Typography>
          </Box>
        )}
        
        {testData && (
          <Box sx={{ bgcolor: '#e8f5e9', p: 2, borderRadius: 1, mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              API Response:
            </Typography>
            <pre style={{ overflow: 'auto' }}>
              {JSON.stringify(testData, null, 2)}
            </pre>
          </Box>
        )}
      </Paper>
      
      <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Calendar Integration Component
        </Typography>
        
        <Divider sx={{ mb: 3 }} />
        
        <CalendarIntegration 
          showConnectOnly={true}
          onCalendarEvent={handleCalendarEvent}
        />
      </Paper>
      
      <Paper elevation={2} sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Add Appointment to Calendar
        </Typography>
        
        <Divider sx={{ mb: 3 }} />
        
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Test Appointment:
          </Typography>
          
          <List>
            <ListItem>
              <ListItemText 
                primary="Title" 
                secondary={mockAppointment.title} 
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Date & Time" 
                secondary={`${mockAppointment.startTime.toLocaleString()} - ${mockAppointment.endTime.toLocaleString()}`} 
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Location" 
                secondary={mockAppointment.location} 
              />
            </ListItem>
          </List>
        </Box>
        
        <CalendarIntegration 
          appointment={mockAppointment}
          onCalendarEvent={handleCalendarEvent}
        />
      </Paper>
    </Box>
  );
};

export default CalendarTest;
